import { useEffect, useRef } from 'react'
import type maplibregl from 'maplibre-gl'
import type { FeatureCollection, Feature, Point } from 'geojson'

/**
 * Saved Other Retailers, drawn as small brand-logo pins.
 *
 * Linear: MYD-485.
 *
 * ---------------------------------------------------------------------------
 * Why this is a symbol layer when every other point on every map is a DOM
 * marker
 * ---------------------------------------------------------------------------
 *
 * `LocationMarker`, `SiteMarkers` and `ClusterMarkers` all build DOM elements
 * and hand them to `new maplibregl.Marker`. That is right for a handful of
 * assets the user owns. It is wrong here, for two reasons, and the second is
 * the one that actually forced it.
 *
 * 1. Volume. A 5-mile ring can hold a hundred saved retailers, and a centre
 *    with tenants multiplies that. Hundreds of absolutely-positioned DOM nodes
 *    re-laid-out on every frame of a pan is visible jank.
 *
 * 2. Collision. DOM markers have NO placement management whatsoever: every one
 *    is drawn, so a dense trade area renders as a pile of overlapping logos
 *    that cannot be read at any zoom below roughly 14. `icon-allow-overlap:
 *    false` hands that thinning to MapLibre's own placement engine, which
 *    drops lower-priority icons as they collide and brings them back as you
 *    zoom in. There is no way to get that behaviour with DOM markers short of
 *    writing a placement engine.
 *
 * The component's SHAPE still matches the house pattern: an effect that owns a
 * GeoJSON source plus layers, toggling the `visibility` layout property rather
 * than unmounting, exactly like `TradeAreaRings` and `DemographicHeatmap`.
 *
 * That matters more than it looks. Basemap switching flips layer visibility
 * instead of calling `map.setStyle()` (see the note in `mapConfig.ts`), which
 * is why the registered images and the source survive a switch to satellite.
 * On a `setStyle`-based codebase every `addImage` here would be silently
 * discarded and the pins would vanish on the first basemap toggle.
 */

export interface RetailerForLayer {
	_id: string
	name: string
	latitude?: number
	longitude?: number
	category?: 'poi' | 'competitor' | 'cotenant'
	logoUrl?: string | null
	monogram?: string
	monogramColor?: string
}

export interface OtherRetailersLayerProps {
	map: maplibregl.Map | null
	isMapLoaded: boolean
	retailers: RetailerForLayer[]
	visible: boolean
	onSelect?: (retailer: RetailerForLayer) => void
	/**
	 * The zoom below which pins are hidden. Defaults to MIN_ZOOM, right for a
	 * map the user zooms. A presentation slide passes 0 (MYD-500): its camera is
	 * fixed, often wider than street level, and the author asked for the
	 * retailers, so hiding them would leave the slide silently without them.
	 * Collision thinning still keeps a wide view readable.
	 */
	minZoom?: number
}

const SOURCE_ID = 'other-retailers-source'
const LAYER_ID = 'other-retailers-symbol-layer'

/**
 * How big a pin actually appears, in CSS pixels.
 *
 * This is the ONE number to change, and everything below derives from it.
 *
 * It is expressed this way because the first version was not, and shipped
 * pins too small to read. Three constants were multiplying: a 48px bitmap, a
 * `pixelRatio: 2` that halves the on-screen size, and an `icon-size: 0.5` that
 * halved it again, landing at 12 CSS px with the logo occupying about 9 of
 * them. Each value looked reasonable alone.
 *
 * 30 rather than 36 is deliberate. `LocationMarker` draws the user's own
 * assets at 36px and `ClusterMarkers` at 32px. Other Retailers are context and
 * should read as subordinate to the things they sit beside, not compete.
 */
export const ICON_DISPLAY_PX = 30

/**
 * The bitmap is drawn at twice the display size and handed to `addImage` with
 * a matching `pixelRatio`, which is what keeps a logo crisp on a retina
 * screen. It also means `icon-size` in the layer MUST stay at 1: the display
 * size is already fully determined by `ICON_BOX / ICON_PIXEL_RATIO`.
 */
export const ICON_PIXEL_RATIO = 2

/**
 * Every icon is rendered into a box this size, logo or monogram alike.
 *
 * Fixing the box is not cosmetic. Brand logos arrive at wildly different
 * aspect ratios (a wide wordmark beside a square glyph), and handing those
 * straight to a uniform `icon-size` makes the wide ones tower over the square
 * ones. Normalising into a padded square is what makes a field of mixed pins
 * look deliberate.
 */
export const ICON_BOX = ICON_DISPLAY_PX * ICON_PIXEL_RATIO

/**
 * Breathing room inside the box, kept tight: legibility of the logo is the
 * whole point, so the mark gets as much of the square as the rounded corners
 * allow.
 */
const ICON_PADDING = Math.round(ICON_BOX * 0.08)

/** Corner radius and monogram type, both proportional so one number governs. */
const ICON_RADIUS = Math.round(ICON_BOX * 0.2)
const MONOGRAM_FONT_PX = Math.round(ICON_BOX * 0.44)

/**
 * Label offset, in ems of `text-size`.
 *
 * The icon is centred on the point, so its bottom edge sits
 * `ICON_DISPLAY_PX / 2` below it. The label is top-anchored, so anything less
 * than that overlaps the pin. Derived rather than guessed, with a couple of
 * pixels of gap.
 */
export const LABEL_TEXT_PX = 11
export const LABEL_OFFSET_EM =
	Math.round((ICON_DISPLAY_PX / 2 + 3) / LABEL_TEXT_PX * 100) / 100

/** Competitors draw above neutral POIs when MapLibre has to drop one. */
const SORT_KEY: Record<string, number> = { competitor: 0, cotenant: 1, poi: 2 }

/** Pins below this are noise: at portfolio zoom the whole ring is a few pixels. */
const MIN_ZOOM = 11

const iconIdFor = (retailer: RetailerForLayer): string =>
	retailer.logoUrl
		? `other-retail-logo-${retailer.logoUrl}`
		: `other-retail-mono-${retailer.monogramColor || 'default'}-${retailer.monogram || '?'}`

/** A rounded square in the brand's colour with its initials. */
function drawMonogram(retailer: RetailerForLayer): ImageData | null {
	const canvas = document.createElement('canvas')
	canvas.width = ICON_BOX
	canvas.height = ICON_BOX
	const ctx = canvas.getContext('2d')
	if (!ctx) return null

	ctx.fillStyle = retailer.monogramColor || '#4A5568'
	ctx.beginPath()
	// `roundRect` is not in every jsdom/browser build; fall back to a plain box
	// rather than losing the pin entirely.
	if (typeof (ctx as any).roundRect === 'function') {
		;(ctx as any).roundRect(0, 0, ICON_BOX, ICON_BOX, ICON_RADIUS)
	} else {
		ctx.rect(0, 0, ICON_BOX, ICON_BOX)
	}
	ctx.fill()

	ctx.fillStyle = '#ffffff'
	ctx.font = `bold ${MONOGRAM_FONT_PX}px system-ui, -apple-system, sans-serif`
	ctx.textAlign = 'center'
	ctx.textBaseline = 'middle'
	ctx.fillText(retailer.monogram || '?', ICON_BOX / 2, ICON_BOX / 2 + 1)

	return ctx.getImageData(0, 0, ICON_BOX, ICON_BOX)
}

/**
 * Load a logo in a way that leaves the canvas readable.
 *
 * Deliberately NOT `map.loadImage`. The drawn pixels have to be read back with
 * `getImageData` for `addImage`, and a canvas that has drawn a cross-origin
 * image without CORS permission is tainted: every read throws SecurityError.
 * Relying on MapLibre's internals left that dependency invisible, and it was
 * the whole of MYD-489.
 *
 * `crossOrigin = 'anonymous'` makes the requirement explicit and makes a
 * non-compliant origin fail LOUDLY at load time rather than quietly at read
 * time. Logo URLs are same-origin (`/api/retail-brands/:slug/logo`), so this
 * always succeeds for them; it is the guard that stops anyone pointing this at
 * a third-party URL again and wondering why the pins went blank.
 */
function loadLogoImage(url: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image()
		img.crossOrigin = 'anonymous'
		img.onload = () => resolve(img)
		img.onerror = () =>
			reject(
				new Error(
					`image would not load with crossOrigin=anonymous, which usually means the ` +
						`origin sends no CORS headers and its pixels could never be read anyway`,
				),
			)
		img.src = url
	})
}

/** A logo, letterboxed onto a white rounded square so every pin is one size. */
function drawLogo(image: CanvasImageSource, width: number, height: number): ImageData | null {
	const canvas = document.createElement('canvas')
	canvas.width = ICON_BOX
	canvas.height = ICON_BOX
	const ctx = canvas.getContext('2d')
	if (!ctx) return null

	ctx.fillStyle = '#ffffff'
	if (typeof (ctx as any).roundRect === 'function') {
		ctx.beginPath()
		;(ctx as any).roundRect(0, 0, ICON_BOX, ICON_BOX, ICON_RADIUS)
		ctx.fill()
	} else {
		ctx.fillRect(0, 0, ICON_BOX, ICON_BOX)
	}

	const available = ICON_BOX - ICON_PADDING * 2
	const scale = Math.min(available / (width || 1), available / (height || 1))
	const drawWidth = (width || available) * scale
	const drawHeight = (height || available) * scale

	ctx.drawImage(
		image,
		(ICON_BOX - drawWidth) / 2,
		(ICON_BOX - drawHeight) / 2,
		drawWidth,
		drawHeight,
	)

	return ctx.getImageData(0, 0, ICON_BOX, ICON_BOX)
}

export function OtherRetailersLayer({
	map,
	isMapLoaded,
	retailers,
	visible,
	onSelect,
	minZoom = MIN_ZOOM,
}: OtherRetailersLayerProps) {
	const initialized = useRef(false)
	/**
	 * Icon ids already handed to `addImage`.
	 *
	 * Load-bearing: `addImage` THROWS on a duplicate id, and logos are shared
	 * across rows by design (forty Starbucks, one icon), so without this the
	 * second row of any repeated brand takes the whole effect down.
	 */
	const addedIcons = useRef<Set<string>>(new Set())
	/** Latest rows, so the click handler never closes over a stale array. */
	const retailersRef = useRef<RetailerForLayer[]>(retailers)
	retailersRef.current = retailers

	useEffect(() => {
		if (!map || !isMapLoaded) return
		let cancelled = false

		const features: Feature<Point>[] = retailers
			.filter(
				(r) =>
					Number.isFinite(Number(r.latitude)) && Number.isFinite(Number(r.longitude)),
			)
			.map((r) => ({
				type: 'Feature' as const,
				geometry: {
					type: 'Point' as const,
					coordinates: [Number(r.longitude), Number(r.latitude)],
				},
				properties: {
					id: r._id,
					name: r.name,
					iconId: iconIdFor(r),
					sortKey: SORT_KEY[r.category ?? 'poi'] ?? 2,
				},
			}))

		const collection: FeatureCollection<Point> = {
			type: 'FeatureCollection',
			features,
		}

		/**
		 * Register one image per DISTINCT icon, not per retailer.
		 *
		 * Async because the image load is. See `loadLogoImage`: it uses an Image
		 * with crossOrigin set rather than `map.loadImage`, because the pixels
		 * must be readable back off the canvas.
		 */
		const registerIcons = async () => {
			for (const retailer of retailers) {
				const iconId = iconIdFor(retailer)
				if (addedIcons.current.has(iconId)) continue
				// Claim the id before awaiting, or two rows of the same brand
				// both pass the check and the second addImage throws.
				addedIcons.current.add(iconId)

				try {
					if (retailer.logoUrl) {
						const image = await loadLogoImage(retailer.logoUrl)
						if (cancelled) return
						const data = drawLogo(image, image.width, image.height)
						if (data && !map.hasImage(iconId)) {
							map.addImage(iconId, data as any, { pixelRatio: ICON_PIXEL_RATIO })
						}
					} else {
						const data = drawMonogram(retailer)
						if (data && !map.hasImage(iconId)) {
							map.addImage(iconId, data as any, { pixelRatio: ICON_PIXEL_RATIO })
						}
					}
				} catch (err) {
					// A logo that will not load must not cost us the pin, so fall
					// back to the monogram under the SAME id and the feature
					// referencing it still resolves to an image.
					//
					// But SAY SO. A silent fallback here hid MYD-489 completely:
					// every logo in the system was failing on a tainted canvas
					// and the map just looked like a map with no logos, because a
					// monogram is also a legitimate outcome.
					console.warn(
						`[OtherRetailers] logo failed for "${retailer.name}" (${retailer.logoUrl}), ` +
							`drawing a monogram instead:`,
						err instanceof Error ? err.message : err,
					)
					try {
						const data = drawMonogram(retailer)
						if (data && !map.hasImage(iconId)) {
							map.addImage(iconId, data as any, { pixelRatio: ICON_PIXEL_RATIO })
						}
					} catch {
						addedIcons.current.delete(iconId)
					}
				}
			}
		}

		const syncSource = () => {
			const existing = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined

			if (!initialized.current || !existing) {
				if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID)
				if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)

				map.addSource(SOURCE_ID, { type: 'geojson', data: collection })
				map.addLayer({
					id: LAYER_ID,
					type: 'symbol',
					source: SOURCE_ID,
					minzoom: minZoom,
					layout: {
						'icon-image': ['get', 'iconId'],
						// MUST stay 1. The on-screen size is already fully
						// determined by ICON_BOX / ICON_PIXEL_RATIO, and the
						// original bug here was a second 0.5 multiplying against
						// that and halving the pins to illegibility.
						'icon-size': 1,
						// The whole reason this is a symbol layer: MapLibre drops
						// colliding icons by sort key instead of piling them up.
						'icon-allow-overlap': false,
						'icon-ignore-placement': false,
						'symbol-sort-key': ['get', 'sortKey'],
						'text-field': ['get', 'name'],
						'text-optional': true,
						'text-size': LABEL_TEXT_PX,
						'text-offset': [0, LABEL_OFFSET_EM],
						'text-anchor': 'top',
						'text-max-width': 9,
					},
					paint: {
						'text-color': '#4A5568',
						'text-halo-color': '#ffffff',
						'text-halo-width': 1.5,
					},
				})
				initialized.current = true
			} else {
				existing.setData(collection)
			}

			if (map.getLayer(LAYER_ID)) {
				map.setLayoutProperty(LAYER_ID, 'visibility', visible ? 'visible' : 'none')
			}
		}

		// Source first, so the layer exists immediately and pins appear as their
		// images arrive rather than after the slowest logo.
		syncSource()
		registerIcons()

		return () => {
			cancelled = true
		}
	}, [map, isMapLoaded, retailers, visible, minZoom])

	// Click and hover. This is the codebase's first handler bound to a layer id
	// rather than to a marker element, so both are removed explicitly.
	useEffect(() => {
		if (!map || !isMapLoaded || !onSelect) return

		const handleClick = (e: any) => {
			const id = e?.features?.[0]?.properties?.id
			if (!id) return
			const hit = retailersRef.current.find((r) => r._id === id)
			if (hit) onSelect(hit)
		}
		const enter = () => {
			map.getCanvas().style.cursor = 'pointer'
		}
		const leave = () => {
			map.getCanvas().style.cursor = ''
		}

		map.on('click', LAYER_ID, handleClick)
		map.on('mouseenter', LAYER_ID, enter)
		map.on('mouseleave', LAYER_ID, leave)

		return () => {
			try {
				map.off('click', LAYER_ID, handleClick)
				map.off('mouseenter', LAYER_ID, enter)
				map.off('mouseleave', LAYER_ID, leave)
			} catch {
				// map may already be torn down
			}
		}
	}, [map, isMapLoaded, onSelect])

	// Cleanup on unmount, matching DemographicHeatmap.
	useEffect(() => {
		return () => {
			if (!map) return
			try {
				if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID)
				if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)
				// Images are deliberately NOT removed. They are keyed by content
				// and shared across every surface on this map instance, so
				// dropping them here would blank pins on a sibling component
				// that is still mounted.
				initialized.current = false
			} catch {
				// map may already be torn down
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return null
}

export default OtherRetailersLayer
